import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Checkbox, Collapse, Divider, FormControlLabel, Grid, IconButton, Link, List, ListItemButton, ListItemIcon, ListItemText, TextField, Typography, } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { MuiPickersAdapterContext } from '@mui/x-date-pickers/LocalizationProvider';
import React, { useContext, useMemo } from 'react';
import { VirtualizedMultiSelect } from './VirtualizedMultiSelect';
import { VirtualizedSelect } from './VirtualizedSelect';
import { getChipLabel } from './getChipLabel';
import { useChip } from './useChip';
export const FilterComponent = React.memo(({ config, value, rawStringValue, error, onChange, onInitialChipLabel }) => {
    let component = React.createElement("div", null, "Unknown filter type");
    switch (config.type) {
        case 'boolean':
            component = (React.createElement(Boolean, { config: config, value: value, onChange: onChange, onInitialChipLabel: onInitialChipLabel }));
            break;
        case 'number':
            component = React.createElement(Number, { config: config, value: value, onChange: onChange, onInitialChipLabel: onInitialChipLabel });
            break;
        case 'string':
            component = (React.createElement(String, { config: config, value: value, rawStringValue: rawStringValue, error: error, onChange: onChange, onInitialChipLabel: onInitialChipLabel }));
            break;
        case 'select-one':
            component = (React.createElement(SelectOne, { config: config, value: value, onChange: onChange, onInitialChipLabel: onInitialChipLabel }));
            break;
        case 'select-many':
            component = (React.createElement(SelectMany, { config: config, value: value, onChange: onChange, onInitialChipLabel: onInitialChipLabel }));
            break;
        case 'select-many-tree':
            component = (React.createElement(SelectManyTree, { config: config, value: value, onChange: onChange, onInitialChipLabel: onInitialChipLabel }));
            break;
        case 'date':
            component = (React.createElement(SelectDate, { config: config, value: value, error: error, onChange: onChange, onInitialChipLabel: onInitialChipLabel }));
            break;
        case 'date-from-to':
            component = (React.createElement(SelectDateFromTo, { config: config, value: value, error: error, onChange: onChange, onInitialChipLabel: onInitialChipLabel }));
            break;
        case 'custom': {
            const CustomComponent = config.component;
            component = React.createElement(CustomComponent, { config: config, value: value, onChange: onChange, onInitialChipLabel: onInitialChipLabel });
            break;
        }
    }
    return React.createElement(Box, { sx: { boxSizing: 'border-box', width: '100%', padding: 2, paddingTop: 0 } }, component);
});
const Boolean = (props) => {
    const chipLabel = (value) => {
        return value ? props.config.label : undefined;
    };
    useChip({
        onInitialChipLabel: props.onInitialChipLabel,
        field: props.config.field,
        chipLabel: chipLabel(props.value),
    });
    return (React.createElement(Box, { mt: -1, mb: -1 },
        React.createElement(FormControlLabel, { control: React.createElement(Checkbox, { color: 'primary', checked: props.value === true, onChange: (event) => props.onChange(props.config.field, event.target.checked || undefined, undefined, undefined, chipLabel(event.target.checked)), "data-test": props.config['data-test'], "aria-label": props.config.label }), label: props.config.label })));
};
const Number = (props) => {
    var _a, _b;
    const chipLabel = (value) => getChipLabel({
        label: props.config.label,
        showLabel: props.config.showChipLabel,
        valueLabel: value,
    });
    useChip({
        onInitialChipLabel: props.onInitialChipLabel,
        field: props.config.field,
        chipLabel: chipLabel((_a = props.value) === null || _a === void 0 ? void 0 : _a.toString()),
    });
    return (React.createElement(TextField, { type: 'number', fullWidth: true, label: props.config.label, size: 'small', value: (_b = props.value) !== null && _b !== void 0 ? _b : '', onChange: (event) => {
            var _a;
            return props.onChange(props.config.field, (_a = parseFloat(event.target.value)) !== null && _a !== void 0 ? _a : undefined, undefined, undefined, chipLabel(event.target.value));
        }, "data-test": props.config['data-test'], "aria-label": props.config.label }));
};
const String = (props) => {
    var _a;
    const chipLabel = (value) => getChipLabel({
        label: props.config.label,
        showLabel: props.config.showChipLabel,
        valueLabel: value,
    });
    useChip({
        onInitialChipLabel: props.onInitialChipLabel,
        field: props.config.field,
        chipLabel: chipLabel(props.value),
    });
    return (React.createElement(TextField, { fullWidth: true, label: props.config.label, size: 'small', error: !!props.error, helperText: props.error, value: (_a = props.rawStringValue) !== null && _a !== void 0 ? _a : '', onChange: (event) => {
            let value = event.target.value;
            let valid = true;
            if (props.config.validateRawStringValue) {
                valid = props.config.validateRawStringValue(value);
            }
            if (valid === true && props.config.parseValue) {
                value = props.config.parseValue(value);
            }
            props.onChange(props.config.field, value, event.target.value, typeof valid === 'string' ? valid : undefined, chipLabel(value));
        }, onBlur: () => {
            var _a, _b;
            if (!props.error && props.config.formatValue && ((_a = props.value) === null || _a === void 0 ? void 0 : _a.length)) {
                props.onChange(props.config.field, props.value, props.config.formatValue((_b = props.value) !== null && _b !== void 0 ? _b : ''));
            }
        }, "data-test": props.config['data-test'], "aria-label": props.config.label }));
};
const SelectOne = (props) => {
    const getSelectedItem = (value) => {
        return props.config.items.find((item) => item.value === value);
    };
    const chipLabel = (value) => {
        var _a;
        return getChipLabel({
            label: props.config.label,
            showLabel: props.config.showChipLabel,
            valueLabel: (_a = getSelectedItem(value)) === null || _a === void 0 ? void 0 : _a.label,
        });
    };
    useChip({
        onInitialChipLabel: props.onInitialChipLabel,
        field: props.config.field,
        chipLabel: chipLabel(props.value),
    });
    const changeHandler = (value) => {
        props.onChange(props.config.field, value || undefined, undefined, undefined, chipLabel(value));
    };
    const mergedProps = Object.assign(Object.assign({}, props), { onChange: changeHandler });
    return React.createElement(VirtualizedSelect, Object.assign({}, mergedProps));
};
const SelectMany = (props) => {
    const getChipLabels = (values) => {
        if (!Array.isArray(values)) {
            return;
        }
        return values.reduce((chipLabels, value) => {
            var _a;
            const label = getChipLabel({
                label: props.config.label,
                showLabel: props.config.showChipLabel,
                valueLabel: (_a = props.config.items.find((item) => item.value === value)) === null || _a === void 0 ? void 0 : _a.label,
            });
            if (!label) {
                return chipLabels;
            }
            if (props.config.showChipLabel && props.config.label) {
                chipLabels.push(`${props.config.label}: ${label}`);
            }
            else {
                chipLabels.push(label);
            }
            return chipLabels;
        }, []);
    };
    useChip({
        onInitialChipLabel: props.onInitialChipLabel,
        field: props.config.field,
        chipLabel: getChipLabels(props.value),
    });
    const onChange = (values) => props.onChange(props.config.field, values.length > 0 ? values : undefined, undefined, undefined, getChipLabels(values));
    if (props.config.type !== 'select-many') {
        return null;
    }
    return React.createElement(VirtualizedMultiSelect, Object.assign({}, props, { onSelectionChange: onChange }));
};
function reduceSelectTreeItems(items) {
    if (!items) {
        return new Map();
    }
    return items.reduce((result, item) => {
        result.set(item.value, item.label);
        const childrenResult = reduceSelectTreeItems(item.children);
        childrenResult.forEach((label, value) => result.set(value, label));
        return result;
    }, new Map());
}
const SelectManyTree = (props) => {
    const reducedItems = useMemo(() => reduceSelectTreeItems(props.config.items), [props.config.items]);
    const getChipLabels = (selectedItems) => {
        if (!selectedItems) {
            return;
        }
        return selectedItems.reduce((chipLabels, selectedItem) => {
            const label = reducedItems.get(selectedItem);
            if (!label) {
                return chipLabels;
            }
            if (props.config.showChipLabel && props.config.label) {
                chipLabels.push(`${props.config.label}: ${label}`);
            }
            else {
                chipLabels.push(label);
            }
            return chipLabels;
        }, []);
    };
    useChip({
        onInitialChipLabel: props.onInitialChipLabel,
        field: props.config.field,
        chipLabel: getChipLabels(props.value),
    });
    const onChange = (values) => {
        props.onChange(props.config.field, values.length > 0 ? values : undefined, undefined, undefined, getChipLabels(values));
    };
    return (React.createElement("div", null,
        props.config.label && React.createElement(Typography, { pb: 1 }, props.config.label),
        React.createElement(List, { sx: {
                maxHeight: 256,
                border: (theme) => '1px solid ' + theme.palette.divider,
                borderRadius: 0.5,
                overflow: 'auto',
            }, dense: true, "data-test": props.config['data-test'] }, Array.isArray(props.config.items) &&
            props.config.items.map((item, index, array) => (React.createElement(React.Fragment, { key: item.value },
                React.createElement(SelectManyTreeRow, { level: 0, item: item, value: props.value, onChange: onChange }),
                index < array.length - 1 && React.createElement(Divider, null)))))));
};
function collectAllValues(items) {
    const itemValues = [];
    function flatten(item) {
        itemValues.push(item.value);
        const children = item.children;
        if (!children || children.length === 0) {
            return;
        }
        children.forEach(flatten);
    }
    items.forEach(flatten);
    return itemValues;
}
const SelectManyTreeRow = (props) => {
    const [expanded, setExpanded] = React.useState(false);
    const children = props.item.children;
    const childrenValues = React.useMemo(() => children && collectAllValues(children), [children]);
    const allChildrenSelected = children && children.length > 0 && (childrenValues === null || childrenValues === void 0 ? void 0 : childrenValues.every((value) => { var _a; return (_a = props.value) === null || _a === void 0 ? void 0 : _a.includes(value); }));
    const atLeastOneChildSelected = children && children.length > 0 && childrenValues && (childrenValues === null || childrenValues === void 0 ? void 0 : childrenValues.some((value) => { var _a; return (_a = props.value) === null || _a === void 0 ? void 0 : _a.includes(value); }));
    const handleClick = (newValue) => {
        var _a;
        const values = (_a = props.value) !== null && _a !== void 0 ? _a : [];
        if (values.includes(newValue)) {
            const newValues = values.filter((value) => value !== newValue);
            props.onChange(newValues);
        }
        else {
            props.onChange([...values, newValue]);
        }
    };
    const handleSelectAllChildren = () => {
        var _a, _b;
        if (childrenValues) {
            if (!allChildrenSelected) {
                const result = props.item.disabled
                    ? [...((_a = props.value) !== null && _a !== void 0 ? _a : []), ...childrenValues]
                    : [...((_b = props.value) !== null && _b !== void 0 ? _b : []), ...childrenValues, props.item.value];
                props.onChange(result);
            }
            else if (props.value) {
                const result = props.value
                    .filter((value) => !childrenValues.includes(value))
                    .filter((value) => value !== props.item.value && !props.item.disabled);
                props.onChange(result);
            }
        }
    };
    const checked = props.value && !props.item.disabled ? props.value.includes(props.item.value) : false;
    return (React.createElement(React.Fragment, null,
        React.createElement(ListItemButton, { sx: {
                paddingLeft: 4 * props.level,
                '&:hover': {
                    '.MuiLink-root': {
                        display: 'block',
                    },
                },
            }, selected: false, onClick: () => {
                if (!expanded) {
                    setExpanded(true);
                }
                if (!props.item.disabled) {
                    handleClick(props.item.value);
                }
            }, "data-test": props.item['data-test'] },
            props.item.children && props.item.children.length > 0 && (React.createElement(Box, { sx: {
                    top: '50%',
                    position: 'absolute',
                    transform: 'translateY(-50%)',
                    left: 4 * props.level + 0.5,
                }, onMouseDown: (event) => event.stopPropagation() },
                React.createElement(IconButton, { onClick: (event) => {
                        event.stopPropagation();
                        setExpanded(!expanded);
                    }, size: 'large' }, expanded ? React.createElement(ExpandLessIcon, null) : React.createElement(ExpandMoreIcon, null)))),
            React.createElement(ListItemIcon, null),
            React.createElement(Checkbox, { indeterminate: atLeastOneChildSelected && !checked, edge: 'start', color: 'primary', checked: checked, tabIndex: -1, disabled: props.item.disabled, disableRipple: true, onClick: (event) => {
                    event.stopPropagation();
                    handleClick(props.item.value);
                } }),
            React.createElement(ListItemText, { sx: {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-word',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 3,
                } }, props.item.label),
            props.item.children && props.item.children.length > 0 && (React.createElement(Link, { onClick: (event) => {
                    event.stopPropagation();
                    handleSelectAllChildren();
                }, sx: { display: 'none', pr: 1 }, component: 'button', type: 'button', variant: 'body2', "data-test": 'select-all-button' }, allChildrenSelected ? 'Deselect All' : 'Select All'))),
        props.item.children && props.item.children.length > 0 && (React.createElement(Collapse, { in: expanded, mountOnEnter: true, unmountOnExit: true }, props.item.children.map((child) => (React.createElement(SelectManyTreeRow, { key: child.value, level: props.level + 1, item: child, value: props.value, onChange: props.onChange })))))));
};
const SelectDate = (props) => {
    var _a;
    let error = props.error ? props.error : undefined;
    const muiPickersAdapterContext = useContext(MuiPickersAdapterContext);
    const useAmPm = typeof props.config.useAmPm === 'boolean' ? props.config.useAmPm : (_a = muiPickersAdapterContext === null || muiPickersAdapterContext === void 0 ? void 0 : muiPickersAdapterContext.utils) === null || _a === void 0 ? void 0 : _a.is12HourCycleInCurrentLocale();
    const getDateValueLabel = (value) => {
        var _a;
        return (_a = muiPickersAdapterContext === null || muiPickersAdapterContext === void 0 ? void 0 : muiPickersAdapterContext.utils) === null || _a === void 0 ? void 0 : _a.format(value, useAmPm ? 'keyboardDateTime12h' : 'keyboardDateTime24h');
    };
    const chipLabel = (value) => {
        return getChipLabel({
            valueLabel: value && !error ? getDateValueLabel(value) : undefined,
            showLabel: props.config.showChipLabel,
            label: props.config.label,
        });
    };
    useChip({
        onInitialChipLabel: props.onInitialChipLabel,
        field: props.config.field,
        chipLabel: chipLabel(props.value),
    });
    const onChange = (date) => {
        let result = undefined;
        if (date) {
            date.setSeconds(0, 0);
            result = date;
        }
        if (date && isNaN(date.valueOf())) {
            error = 'Invalid Date';
        }
        else {
            error = undefined;
        }
        props.onChange(props.config.field, result, undefined, error, chipLabel(result));
    };
    return (React.createElement(DateTimePicker, { slotProps: {
            textField: {
                fullWidth: true,
                error: !!error,
                helperText: error,
            },
        }, ampm: useAmPm, disableFuture: props.config.disableFuture, label: props.config.label, value: props.value || null, onChange: (date) => onChange(date) }));
};
const SelectDateFromTo = (props) => {
    var _a, _b, _c, _d, _e;
    const errors = props.error ? JSON.parse(props.error) : {};
    const muiPickersAdapterContext = useContext(MuiPickersAdapterContext);
    const useAmPm = typeof props.config.useAmPm === 'boolean' ? props.config.useAmPm : (_a = muiPickersAdapterContext === null || muiPickersAdapterContext === void 0 ? void 0 : muiPickersAdapterContext.utils) === null || _a === void 0 ? void 0 : _a.is12HourCycleInCurrentLocale();
    const getDateValueLabel = (value) => {
        var _a;
        return (_a = muiPickersAdapterContext === null || muiPickersAdapterContext === void 0 ? void 0 : muiPickersAdapterContext.utils) === null || _a === void 0 ? void 0 : _a.format(value, useAmPm ? 'keyboardDateTime12h' : 'keyboardDateTime24h');
    };
    const getChipLabels = (value) => {
        const dateLabel = [];
        if (!value || Object.keys(errors).length !== 0) {
            return;
        }
        if (value === null || value === void 0 ? void 0 : value.dateFrom) {
            const date = getDateValueLabel(value.dateFrom);
            if (date) {
                const dateFromLabel = props.config.showChipLabel ? `${props.config.fromLabel}: ${date}` : date;
                dateLabel.push(dateFromLabel);
            }
        }
        if (value === null || value === void 0 ? void 0 : value.dateTo) {
            const date = getDateValueLabel(value.dateTo);
            if (date) {
                const dateToLabel = props.config.showChipLabel ? `${props.config.toLabel}: ${date}` : date;
                dateLabel.push(dateToLabel);
            }
        }
        return dateLabel.join(', ');
    };
    useChip({
        onInitialChipLabel: props.onInitialChipLabel,
        field: props.config.field,
        chipLabel: getChipLabels(props.value),
    });
    const onChange = (number, date) => {
        const result = props.value ? Object.assign({}, props.value) : {};
        if (number === 0) {
            result.dateFrom = undefined;
            if (date) {
                date.setSeconds(0, 0);
                result.dateFrom = date;
            }
        }
        else {
            result.dateTo = undefined;
            if (date) {
                date.setSeconds(0, 0);
                result.dateTo = date;
            }
        }
        if (date && isNaN(date.valueOf())) {
            errors[number] = 'Invalid date';
        }
        else if (result.dateFrom && result.dateTo && result.dateFrom >= result.dateTo) {
            if (number === 0) {
                errors[number] = `${props.config.fromLabel} must be before ${props.config.toLabel}`;
            }
            else {
                errors[number] = `${props.config.toLabel} must be after ${props.config.fromLabel}`;
            }
            delete errors[Math.abs(number - 1)];
        }
        else {
            delete errors[number];
        }
        props.onChange(props.config.field, result.dateFrom || result.dateTo ? Object.assign({}, result) : undefined, undefined, Object.keys(errors).length !== 0 ? JSON.stringify(errors) : undefined, getChipLabels(result));
    };
    return (React.createElement(Grid, { container: true, justifyContent: 'space-between', spacing: 1, "data-test": props.config['data-test'] },
        React.createElement(Grid, { item: true, xs: 12, sm: 6 },
            React.createElement(DateTimePicker, { slotProps: {
                    textField: {
                        fullWidth: true,
                        error: !!errors[0],
                        helperText: errors[0],
                    },
                }, ampm: useAmPm, disableFuture: props.config.disableFuture, label: props.config.fromLabel, value: (_c = (_b = props.value) === null || _b === void 0 ? void 0 : _b.dateFrom) !== null && _c !== void 0 ? _c : null, onChange: (date) => onChange(0, date) })),
        React.createElement(Grid, { item: true, xs: 12, sm: 6 },
            React.createElement(DateTimePicker, { slotProps: {
                    textField: {
                        fullWidth: true,
                        error: !!errors[1],
                        helperText: errors[1],
                    },
                }, ampm: useAmPm, disableFuture: props.config.disableFuture, label: props.config.toLabel, value: (_e = (_d = props.value) === null || _d === void 0 ? void 0 : _d.dateTo) !== null && _e !== void 0 ? _e : null, onChange: (date) => onChange(1, date) }))));
};
